import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Box,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import { updateOrCreateUserSetting } from "../../../reducers/UserSettingsReducer";
import { AMSModules, paginationStyle, serviceNames } from "../../../utils/constants";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import {
  formatDateToMonthDateYear,
  getDaysUntil,
  getEmployeeOptionLabel,
  getRowsPerPageOptions,
  mapDictionaryOptions,
} from "../../../utils/functions";
import { getService } from "../../../reducers/service";
import {
  deleteTrainingCertification,
  getTrainingCertifications,
} from "../../../reducers/TrainingCertificationReducer";
import CertificationTableToolbar from "./CertificationTableToolbar";
import {
  PATH_TRAINING_CERTIFICATIONS_ADD,
  PATH_TRAINING_CERTIFICATIONS_EDIT,
} from "../../../utils/routeConstants";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import PATViewButton from "../../../components/Buttons/PATViewButton";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
  employee: "",
  certificationType: "",
  employeeStatus: "",
  dateObtainedFrom: "",
  dateObtainedTo: "",
  expireDateFrom: "",
  expireDateTo: "",
  tagTypes: [],
};

const Certifications = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();

  const { trainingCertifications, loading, total } = useSelector(
    (state) => state.trainingCertifications
  );
  const { userSettings } = useSelector((state) => state.userSettings);
  const themeMode = useSelector((state) => state.theme.mode);
  const { userData, isAuthenticated } = useSelector((state) => state.user);
  const userAgency = useSelector((state) => state.user.userAgency);
  const filtersInfo = useSelector((state) => state.filtersInfo);

  const [page, setPage] = useState(INITIAL_PAGE);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
  const [selectedId, setSelectedId] = useState(null);
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [columnsSize, setColumnsSize] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filters, setFilters] = useState(filtersInfo.certifications || initialFilterState);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [certificationTypeOptions, setCertificationTypeOptions] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const currentAgency = useRef(null);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
  const employmentStatusOptions = mapDictionaryOptions(
    dictionary.codePerEmploymentStatuses
  );

  const certificatePermission = getAccessPermission(
    "training",
    "Certifications",
    "any"
  );
  const canReadCertificate = certificatePermission?.Read ?? true;
  const canEditCertificate = certificatePermission?.Edit ?? true;
  const canDeleteCertificate = certificatePermission?.Delete ?? true;

  const fetchDropdownValues = async () => {
    const services = {
      distinct: getService(serviceNames.perTrainingCertificationDistinct),
      employees: getService(serviceNames.personnel),
      employmentStatusService: getService(serviceNames.codePerEmploymentStatus),
      tagType: getService(serviceNames.codePerTags),
    };

    const [employeesResult, distinctResult] = await Promise.all([
      services.employees.find({
        query: {
          $limit: -1,
          isDeleted: false,
          $sort: { lastName: 1, firstName: 1, middleName: 1 },
          agencyId: userAgency,
        },
      }),
      services.distinct.find({
        query: {
          column: ["Employee", "CertificationType"],
          agencyId: userAgency,
        },
      }),
      services.employmentStatusService.find({
        query: { $limit: -1, isDeleted: false, agencyId: userAgency },
      }),
    ]);

    const distinctEmployees = new Set(distinctResult?.Employee || []);
    const allEmployees = employeesResult || [];

    const employeeOptions = allEmployees
      .filter((emp) => distinctEmployees.has(String(emp.employeeNum).trim()))
      .map((emp) => ({
        label: getEmployeeOptionLabel(emp),
        value: emp.employeeNum,
      }));
    const certificationTypeOptions =
      distinctResult?.CertificationType.map((value) => ({
        label: value,
        value,
      })) || [];

    setEmployeeOptions(employeeOptions);
    setCertificationTypeOptions(certificationTypeOptions);
  };

  useEffect(() => {
    if (!settingsLoaded || !isAuthenticated) return;

    const { field, sort } = sortModel[0] || {};
    if (field && sort) {
      const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}
      dispatch(
        getTrainingCertifications(
          page,
          pageSize,
          field,
          sort,
          isAgencyChanged ? initialFilterState : filters,
          userAgency
        )
      );
    }
  }, [
    dispatch,
    page,
    pageSize,
    sortModel,
    filters,
    settingsLoaded,
    isAuthenticated,
    userAgency,
  ]);

  const baseColumns = useMemo(
    () => [
      {
        field: "certificateNumber",
        headerName: "Certificate #",
        width: 150,
        sortable: true,
      },
      {
        field: "dateObtained",
        headerName: "Obtained",
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          return formatDateToMonthDateYear(params.value);
        },
      },
      {
        field: "certificationType",
        headerName: "Type",
        width: 150,
        sortable: true,
      },
      {
        field: "employee",
        headerName: "Employee",
        width: 150,
        sortable: true,
        valueGetter: (params) => {
          const details = params.row?.employeeDetails;
          if (!details || typeof details !== "object") {
            return params.row?.employee || "";
          }

          const firstName = details.firstName || "";
          const lastName = details.lastName || "";
          const middleName = details.middleName || "";

          const fullName = [lastName, firstName, middleName]
            .filter((name) => name && typeof name === "string")
            .join(" ")
            .trim();

          return fullName;
        },
      },

      {
        field: "expireDate",
        headerName: "Expiration",
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          return formatDateToMonthDateYear(params.value);
        },
      },
      {
        field: "daysUntilExpiration",
        headerName: "Days Until",
        width: 150,
        sortable: false,
        valueGetter: (params) => getDaysUntil(params.row.expireDate),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 120,
        align: "center",
        sortable: false,
        renderCell: ({ row }) =>
          row.id ? (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canEditCertificate && (
                      <PATEditButton
                        tooltipText="Edit Certification"
                        onClick={() =>
                          history.push(
                            `${PATH_TRAINING_CERTIFICATIONS_EDIT}${row.id}`
                          )
                        }
                        size="20px"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canReadCertificate && !canEditCertificate && (
                      <PATViewButton
                        tooltipText="View Certficate Details"
                        onClick={() =>
                          history.push({
                            pathname: `${PATH_TRAINING_CERTIFICATIONS_EDIT}${row.id}`,
                            search: "?readOnly=true",
                          })
                        }
                        size="20px"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canDeleteCertificate && (
                      <PATDeleteButton
                        tooltipText="Delete Certification"
                        onClick={() => {
                          setSelectedId(row.id);
                          setOpenDeleteDialog(true);
                        }}
                        size="20px"
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null,
      },
    ],
    [history, canDeleteCertificate, canEditCertificate, canReadCertificate]
  );

  const columns = useMemo(() => {
    const hasOrder = columnsOrder.length > 0;
    const hasSizes = Object.keys(columnsSize).length > 0;

    if (!hasOrder && !hasSizes) return baseColumns;

    let baseOrderedColumns;

    if (hasOrder) {
      const ordered = columnsOrder
        .map((field) => baseColumns.find((col) => col.field === field))
        .filter(Boolean);

      const remaining = baseColumns.filter(
        (col) => !columnsOrder.includes(col.field)
      );

      const actionsIndex = ordered.findIndex((col) => col.field === "actions");
      const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

      baseOrderedColumns = [
        ...ordered.slice(0, insertAt),
        ...remaining,
        ...ordered.slice(insertAt),
      ];
    } else {
      baseOrderedColumns = baseColumns;
    }

    const finalColumns = baseOrderedColumns.map((col) => ({
      ...col,
      width: columnsSize[col.field] ?? col.width ?? 100,
    }));

    return finalColumns;
  }, [baseColumns, columnsOrder, columnsSize]);

  const allColumnFields = baseColumns.map((col) => col.field);
  useEffect(() => {
    const defaultVisibility = {};
    currentAgency.current = userAgency;
    allColumnFields.forEach((field) => {
      defaultVisibility[field] = true;
    });
    setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || settingsLoaded) return;

    const initialize = async () => {
      await fetchDropdownValues();
      const settingsMap = Object.fromEntries(
        userSettings.map((s) => [s.key, s.value])
      );
      if (settingsMap["trainingCertificationTableColumnOrder"]) {
        setColumnsOrder(
          JSON.parse(settingsMap["trainingCertificationTableColumnOrder"])
        );
      }

      if (settingsMap["trainingCertificationTableColumnSizes"]) {
        const widthArray = JSON.parse(
          settingsMap["trainingCertificationTableColumnSizes"]
        );
        const result = widthArray.reduce((acc, curr) => {
          acc[curr.field] = curr.width;
          return acc;
        }, {});
        setColumnsSize(result);
      }

      if (settingsMap["trainingCertificationTableColumnVisibility"]) {
        setColumnVisibilityModel(
          JSON.parse(settingsMap["trainingCertificationTableColumnVisibility"])
        );
      }

      if (settingsMap["trainingCertificationTableSortModel"]) {
        const savedSortModel = JSON.parse(
          settingsMap["trainingCertificationTableSortModel"]
        );
        if (Array.isArray(savedSortModel)) {
          const validSortModel = savedSortModel.filter((model) =>
            baseColumns.some((col) => col.field === model.field)
          );
          if (validSortModel.length > 0) {
            setSortModel(savedSortModel);
          }
        }
      }
      setSettingsLoaded(true);
    };
    initialize();
  }, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

  useEffect(() => {
    if (!isAuthenticated || !userAgency) return;
    fetchDropdownValues();
  }, [userAgency, isAuthenticated]);

  const handleSortModelChange = useCallback(
    (model) => {
      const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

      // Avoid unnecessary state updates if sort model didn't change
      const isSortModelDifferent =
        JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

      if (isSortModelDifferent) {
        setSortModel(newSortModel);
        setPage(INITIAL_PAGE);

        if (userData?.user?.ptsUserID) {
          dispatch(
            updateOrCreateUserSetting(
              userData.user.ptsUserID,
              "trainingCertificationTableSortModel",
              JSON.stringify(newSortModel)
            )
          );
        }
      }
    },
    [dispatch, userData?.user?.ptsUserID, sortModel]
  );

  const handleColumnOrderChange = useCallback(
    (params, event, details) => {
      const newColumnOrder = details.api
        .getAllColumns()
        .map((col) => col.field);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "trainingCertificationTableColumnOrder",
            JSON.stringify(newColumnOrder)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  const handleColumnWidthChange = (params, event, details) => {
    const columnSizes = details.api.getAllColumns().map((col) => {
      return {
        field: col.field,
        width: col.width,
      };
    });
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          "trainingCertificationTableColumnSizes",
          JSON.stringify(columnSizes)
        )
      );
    }
  };

  const handleColumnVisibilityModelChange = useCallback(
    (newModel) => {
      setColumnVisibilityModel(newModel);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "trainingCertificationTableColumnVisibility",
            JSON.stringify(newModel)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  const handleDelete = useCallback(() => {
    if (selectedId) {
      dispatch(
        deleteTrainingCertification(selectedId, userData?.user?.ptsUserID)
      );
      setOpenDeleteDialog(false);
      setSelectedId(null);
      fetchDropdownValues();
    }
  }, [dispatch, selectedId, userData?.user?.ptsUserID]);

  const resetFilters = () => {
    setFilters(initialFilterState);
    dispatch({ type: AMSModules.Certifications, payload: initialFilterState });
  };

  const handleOnFilterChange = (newFilters) => {
    setFilters(newFilters);
    dispatch({ type: AMSModules.Certifications, payload: newFilters });
  }

  const rowsPerPageOptions = useMemo(
    () => getRowsPerPageOptions(total),
    [total]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 85px)", // or any other dynamic value subtracting top nav height
        marginTop: theme.spacing(0.5),
      }}
    >
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          m: 1,
          p: 2,
        }}
      >
        <CertificationTableToolbar
          onAddClick={() => history.push(`${PATH_TRAINING_CERTIFICATIONS_ADD}`)}
          certifications={trainingCertifications || []}
          onFilterChange={handleOnFilterChange}
          filters={filters}
          resetFilters={resetFilters}
          employeeOptions={employeeOptions}
          certificationTypeOptions={certificationTypeOptions}
          employmentStatusOptions={employmentStatusOptions}
          tagTypeOptions={tagTypeOptions}
        />

        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGridPro
            rows={trainingCertifications || []}
            columns={columns}
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            getRowId={(row) => row.GUID}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination
            paginationMode="server"
            sortModel={sortModel}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            rowCount={total}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
            disableSelectionOnClick
            onColumnOrderChange={handleColumnOrderChange}
            onColumnWidthChange={handleColumnWidthChange}
            sx={{
              ...paginationStyle(theme, themeMode),
              color: theme.palette.colorFont,
              margin: 1,
            }}
          />
        </Box>
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setSelectedId(null);
          }}
          onConfirm={handleDelete}
          title="Delete Certification"
          message="Are you sure you want to delete this training certification?"
        />
      </Paper>
    </Box>
  );
};

export default Certifications;
